import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Star, Check, ShieldCheck, Truck, RefreshCcw, ShoppingBag } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CloverCheckoutModal } from "../components/CloverCheckoutModal";
import { PRODUCT, IMAGES, CHECKOUT_URL } from "../lib/brand";

const gallery = [
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/viuq0x70_Front%20Box%20CLEAR.webp",
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/vz31653b_studio%20shot%20CLEAR.webp",
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/j4d43ab2_Side%20Box%201%20CLEAR.webp",
];

const Shop = () => {
  const [active, setActive] = useState(0);
  const [color, setColor] = useState(PRODUCT.colorways[0]);
  const [qty, setQty] = useState(1);
  const [modal, setModal] = useState(false);

  return (
    <div className="bg-[#F5F5F5]" data-testid="shop-page">
      <section className="mx-auto max-w-[1400px] px-5 pb-24 pt-32 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Gallery */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-white to-[#E9E9E9]">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_25%,rgba(238,64,38,0.12),transparent_60%)]" />
                <motion.img
                  key={active}
                  src={gallery[active]}
                  alt="Featherbomb"
                  data-testid="product-image-gallery"
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="relative z-10 aspect-square w-full object-contain p-6"
                />
              </div>
            </Reveal>
            <div className="mt-4 flex gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`overflow-hidden rounded-xl border-2 transition ${
                    active === i ? "border-[#EE4026]" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt="" className="h-20 w-20 bg-white object-contain" />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[#184786] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                {PRODUCT.edition}
              </span>
              <span className="flex items-center gap-1 text-sm text-[#212121]/70">
                <Star size={15} className="fill-[#EE4026] text-[#EE4026]" />
                {PRODUCT.rating} · {PRODUCT.reviews.toLocaleString()} reviews
              </span>
            </div>

            <h1 className="font-display mt-4 text-5xl uppercase leading-[0.9] text-[#212121] sm:text-7xl" data-testid="product-title">
              Feather<span className="text-[#EE4026]">bomb</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#212121]/70">{PRODUCT.blurb}</p>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-4xl text-[#212121]" data-testid="product-price">
                ${PRODUCT.price.toFixed(2)}
              </span>
            </div>

            {/* Colorways */}
            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#212121]/60">{color.name}</p>
              <div className="mt-3 flex gap-3" data-testid="product-color-selector">
                {PRODUCT.colorways.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`product-color-${c.id}`}
                    onClick={() => setColor(c)}
                    className={`grid h-12 w-12 place-items-center rounded-full border-2 transition ${
                      color.id === c.id ? "border-[#212121]" : "border-transparent"
                    }`}
                  >
                    <span
                      className="h-8 w-8 rounded-full ring-2"
                      style={{ background: c.hex, "--tw-ring-color": c.ring, boxShadow: `inset 0 0 0 3px ${c.ring}` }}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity + buy */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-full border border-black/15 bg-white">
                <button
                  data-testid="product-qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="grid h-12 w-12 place-items-center text-[#212121] hover:text-[#EE4026]"
                >
                  <Minus size={18} />
                </button>
                <span data-testid="product-quantity-input" className="w-10 text-center font-bold">{qty}</span>
                <button
                  data-testid="product-qty-increase"
                  onClick={() => setQty((q) => Math.min(10, q + 1))}
                  className="grid h-12 w-12 place-items-center text-[#212121] hover:text-[#EE4026]"
                >
                  <Plus size={18} />
                </button>
              </div>

              <a
                href={CHECKOUT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="product-buy-clover-button"
                className="btn-primary flex flex-1 items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wide"
              >
                <ShoppingBag size={18} />
                Buy Now
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-7 grid grid-cols-3 gap-3">
              {[
                [Truck, "Free local pick up in Vancouver, BC and fast shipping Canada-wide"],
                [RefreshCcw, "30-day returns"],
                [ShieldCheck, "Secure Clover pay"],
              ].map(([Icon, t]) => (
                <div key={t} className="flex flex-col items-center gap-2 rounded-2xl border border-black/10 bg-white p-4 text-center">
                  <Icon size={18} className="text-[#184786]" />
                  <span className="text-[11px] font-semibold leading-tight text-[#212121]/70">{t}</span>
                </div>
              ))}
            </div>

            {/* Specs */}
            <div className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#212121]/60">Tech specs</p>
              <div className="mt-4 divide-y divide-black/10 rounded-2xl border border-black/10 bg-white">
                {PRODUCT.specs.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-5 py-3.5 text-sm">
                    <span className="text-[#212121]/60">{k}</span>
                    <span className="font-semibold text-[#212121]">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 space-y-2">
              {["In stock — ships today", "Weatherproof for beach & court", "Loved by players worldwide"].map((t) => (
                <p key={t} className="flex items-center gap-2 text-sm text-[#212121]/70">
                  <Check size={16} className="text-[#EE4026]" /> {t}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CloverCheckoutModal open={modal} onClose={() => setModal(false)} colorway={color} qty={qty} />
    </div>
  );
};

export default Shop;
